import type { Entity } from '@backstage/catalog-model';
import type { DocumentationFilter, DocumentationType } from './types';

export const getDocumentationType = (
  entity: Entity,
): DocumentationType | null => {
  const type = entity.spec?.type;
  return type === 'documentation' || type === 'library' ? type : null;
};

export const getOwnerLabel = (entity: Entity): string => {
  const owner = entity.spec?.owner;
  return typeof owner === 'string'
    ? owner.split('/').pop() || 'Sin equipo'
    : 'Sin equipo';
};

export const getTags = (entity: Entity): string[] => entity.metadata.tags ?? [];

const getItems = (data: unknown): unknown[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (
    data &&
    typeof data === 'object' &&
    'items' in data &&
    Array.isArray(data.items)
  ) {
    return data.items;
  }
  return [];
};

export const getDocumentationEntities = (data: unknown): Entity[] => {
  return (getItems(data) as Entity[])
    .filter(entity => {
      const hasTechDocs = Boolean(
        entity.metadata.annotations?.['backstage.io/techdocs-ref'],
      );
      return hasTechDocs && getDocumentationType(entity) !== null;
    })
    .sort((first, second) => {
      const firstType = getDocumentationType(first);
      const secondType = getDocumentationType(second);
      if (firstType === 'documentation' && secondType !== firstType) {
        return -1;
      }
      if (firstType !== 'documentation' && secondType === 'documentation') {
        return 1;
      }
      return (first.metadata.title || first.metadata.name).localeCompare(
        second.metadata.title || second.metadata.name,
      );
    });
};

export const filterDocumentationEntities = (
  entities: Entity[],
  query: string,
  selectedFilter: DocumentationFilter,
): Entity[] => {
  const normalizedQuery = query.trim().toLowerCase();

  return entities.filter(entity => {
    if (
      selectedFilter !== 'all' &&
      getDocumentationType(entity) !== selectedFilter
    ) {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }

    return [
      entity.metadata.title,
      entity.metadata.name,
      entity.metadata.description,
      ...getTags(entity),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalizedQuery);
  });
};

export const countDocumentationByType = (
  entities: Entity[],
  type: DocumentationFilter,
): number =>
  type === 'all'
    ? entities.length
    : entities.filter(entity => getDocumentationType(entity) === type).length;
